import * as React from 'react';

import classnames from 'classnames';

import {Pencil} from 'shells/icon';

import {EditableLabelWrapping} from '../editable-label/editable-label-wrapping';
import {DescriptionBody} from '../typography/description-body';
import {useRefObserver} from '../utils/hooks/use-ref-observer';

import {ErrorMessage} from './error-message';

import styles from './form-field.module.css';

type Props = {
    editableLabelProps: {
        onSaveNewValue: (arg1: string) => Promise<any>;
        placeholder: string;
        allowSavingEmpty: boolean;
        shouldAllowUndo: boolean;
        required?: boolean;
        tooltip?: string | null | undefined;
        shouldFocusOnMount?: boolean;
        onFocusedOnMount?: () => void;
    };
    label: string;
    /** The id of the main form element, used to connect the label to it */
    innerId: string;
    /** Text to display in red under the main form element */
    errorMessage?: string | null | undefined;
    /** Display with no margin */
    noMargin?: boolean | null | undefined;
    /** Text to display in grey under the main form element, if no error */
    helperText?: string | null | undefined | React.ReactNode;
    tooltipInfo?: string | React.ReactNode;
    isFullWidth?: boolean;
    smallBottomMargin?: boolean;
    /** The main form element */
    children: React.ReactNode;
    isActive?: boolean;
    onEditField?: () => void;
};

export function EditableFormField(props: Props) {
    const fieldRef = React.useRef(null);
    const {isHovered, isChildFocused} = useRefObserver(fieldRef, ['isHovered', 'isChildFocused']);

    const styleName = classnames(styles['form-field'], {
        [styles['form-field--with-footer']]: props.errorMessage || props.helperText,
        [styles['form-field--no-margin']]: props.noMargin,
        [styles['form-field--full-width']]: props.isFullWidth,
    });

    return (
        <div
            style={props.smallBottomMargin ? {marginBottom: '16px'} : undefined}
            className={styleName}
        >
            <EditableLabelWrapping
                {...props.editableLabelProps}
                value={props.label}
                // @ts-expect-error
                tooltip={null}
                isEditableIconVisible={props.isActive}
            />
            {props.errorMessage ? <ErrorMessage>{props.errorMessage}</ErrorMessage> : undefined}
            <div ref={fieldRef} className='pos-relative'>
                {props.children}
                {props.onEditField && (props.isActive || isHovered) && !isChildFocused && (
                    <div onClick={props.onEditField} className={styles.edit}>
                        <Pencil data-focus-element='input' size={12} />
                    </div>
                )}
            </div>
            {props.helperText && !props.errorMessage ? (
                <DescriptionBody>{props.helperText}</DescriptionBody>
            ) : undefined}
        </div>
    );
}
